import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal/Modal';
import AddEntity from '../../components/AddEntity/AddEntity';
import ApiManager from '../../ApiManager/ApiManager';
import { useComponentLoading } from '../../utils/useComponentLoading';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';

const NewExpenditure = ({ onSuccess }) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [carOptions, setCarOptions] = useState([]);
    const [driverOptions, setDriverOptions] = useState([]);
    const [expenditureFields, setExpenditureFields] = useState([]);
    const [carMap, setCarMap] = useState({});
    const [driverMap, setDriverMap] = useState({});
    const [fileResponse, setFileResponse] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    
    // Use the loading hook
    const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

    const fetchDriverData = async () => {
        try {
            const params = new URLSearchParams({
                page_size: 1000,
                page: 1,
            });
            const response = await ApiManager.getAllDrivers(params);
            if (!response.ok) throw new Error('Network response was not ok');
            const json = await response.json();
            const driverNames = json.map((driver) => driver.firstName);
            const driverIdMap = json.reduce((map, driver) => {
                map[driver.firstName] = driver.id;
                return map;
            }, {});
            setDriverOptions(driverNames);
            setDriverMap(driverIdMap);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    };

    const fetchCarData = async () => {
        try {
            const params = new URLSearchParams({
                page_size: 1000,
                page: 1,
            });
            const response = await ApiManager.getAllCars(params);
            if (!response.ok) throw new Error('Network response was not ok');
            const json = await response.json();
            const carNumbers = json.map((car) => car.vehicleNo);
            const carIdMap = json.reduce((map, car) => {
                map[car.vehicleNo] = car.id;
                return map;
            }, {});
            setCarOptions(carNumbers);
            setCarMap(carIdMap);
        } catch (error) {
            console.error('Error fetching cars:', error);
        }
    };

    // Add a function to load all required data
    const loadData = async () => {
        if (dataLoaded) return;
        
        setShowLoading(true);
        
        await withLoading('loadNewExpenditureData', async (onProgress) => {
            onProgress(10);
            await fetchDriverData();
            onProgress(50);
            await fetchCarData();
            onProgress(100);
            
            // Add a small delay to ensure the loading indicator is visible
            setTimeout(() => {
                setDataLoaded(true);
                setShowLoading(false);
            }, 500);
        }, 'Loading driver and car data...', 30000);
    };

    useEffect(() => {
        setExpenditureFields([
            ...(carOptions.length > 0
                ? [{ label: 'Car', name: 'car_id', type: 'picklist', required: true, options: carOptions }]
                : [{ label: 'Car', name: 'car_id', type: 'picklist', required: true, options: [] }]),
            ...(driverOptions.length > 0
                ? [{ label: 'Driver', name: 'driver_id', type: 'picklist', required: true, options: driverOptions }]
                : [{ label: 'Driver', name: 'driver_id', type: 'picklist', required: true, options: [] }]),
            { label: 'Amount', name: 'amount', type: 'number', required: true },
            { label: 'Description', name: 'description', type: 'text', required: false },
            { label: 'Receipt', name: 'receipt', type: 'file', required: false },
            { label: 'Driver Paid', name: 'is_paid_by_driver', type: 'checkbox', required: false },
            { label: 'Car Related', name: 'is_car_related', type: 'checkbox', required: false },
        ]);
    }, [carOptions, driverOptions]);

    const toggleModal = async () => {
        setModalOpen(!isModalOpen);
        if (!isModalOpen) {
            await loadData();
        }
    };

    const handleAddExpenditure = async (formData) => {
        const carId = carMap[formData.car_id];
        const driverId = driverMap[formData.driver_id];

        const updatedFormData = {
            ...formData,
            car_id: carId,
            driver_id: driverId
        };

        if (fileResponse?.receipt) {
            updatedFormData.filepath = fileResponse.receipt;
        }

        const response = await ApiManager.addExpenditure(updatedFormData);
        return response;
    };

    return (
        <div>
            
                <button className="custom-button" onClick={toggleModal}>
                    New Expenditure
                </button>
            
            <Modal isOpen={isModalOpen} onClose={toggleModal} title="Add Expenditure">
                {showLoading ? (
                    <div className="loading-container">
                        <LoadingSpinner 
                            size="large" 
                            message={getMessage('loadNewExpenditureData') || "Loading data..."} 
                            progress={getProgress('loadNewExpenditureData')}
                            showProgress={true}
                        />
                    </div>
                ) : (
                    <AddEntity
                        toggleFunction={toggleModal}
                        formFields={expenditureFields}
                        apiFunction={handleAddExpenditure}
                        setFileResponse={setFileResponse}
                        fileResponse={fileResponse}
                        onSuccess={onSuccess}
                    />
                )}
            </Modal>
        </div>
    );
};

export default NewExpenditure;
